// Pure permission-checking utilities.
//
// How the backend RBAC works (three-layer reality):
//   1. DB: Permission table (permission_code strings, e.g. "content.publish")
//          RolePermission table (role → permission_id, allowed bool — CONFIGURABLE per admin)
//   2. Backend dependency: require_permission("content.publish") — checks DB/Redis, authoritative
//   3. Frontend: UX optimisation only — hides buttons that would 403 anyway
//
// The frontend learns permission codes by fetching them from the API after login.
// Until fetched (permissions === null), only super_admin is allowed anything.
// NEVER rely on frontend permission checks for security — the backend always wins.

import type { AdminRole } from '@/types/auth';

/**
 * Returns true if the given fetched permissions list includes the requested action.
 * super_admin bypasses all checks (mirrors backend require_permission behaviour).
 * permission codes use dot notation: "content.publish", "admin.permissions.view".
 */
export function checkPermission(
  role: AdminRole,
  permissions: string[] | null,
  action: string,
): boolean {
  if (role === 'super_admin') return true;
  // Conservative default: deny when permissions haven't been fetched yet.
  if (permissions === null) return false;
  return permissions.includes(action);
}

// Actions is an array (not rest params) to avoid spread type errors at call sites.

export function checkAnyPermission(
  role: AdminRole,
  permissions: string[] | null,
  actions: string[],
): boolean {
  return actions.some((a) => checkPermission(role, permissions, a));
}

export function checkAllPermissions(
  role: AdminRole,
  permissions: string[] | null,
  actions: string[],
): boolean {
  return actions.every((a) => checkPermission(role, permissions, a));
}
